import React, { useState, useLayoutEffect } from 'react';
import SlideshowImage from 'modules/SlideshowImage';
import useFlags from '@/hooks/useFlags';

const { displaySlideGroupHeaders } = useFlags();

interface UseImageZoomProps {
  imageModels: { image: { id: string }, zoomType: string }[];
  ref: React.MutableRefObject<HTMLDivElement>;
  skip: boolean;
}

const useImageZoom = ({ imageModels = [], ref, skip }: UseImageZoomProps) => {
  const [slideshowImages, setSlideshowImages] = useState([]);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [questionImagesLoaded, setQuestionImagesLoaded] = useState(false);

  const shouldUseZoomIcon = (img: HTMLImageElement) => {
    const match = img.src.match(/\/(\d+)\//);
    const number = match ? match[1] : null;
    const imageModel = imageModels.find(im => im.image.id.toString() === number?.toString());

    return (!!imageModel?.zoomType || img.className === 'vocabImage') && displaySlideGroupHeaders;
  };

  let timeout;

  useLayoutEffect(() => {
    if (skip) return undefined;
    if (!ref || !ref.current) return undefined;

    // Add an event listener to the iframe to set the loaded state.
    const iframe = ref.current.querySelector('iframe');
    if (iframe) {
      iframe.addEventListener('load', () => setIframeLoaded(true));
    }

    // For drag and drop images, we need to wait for the questionLoaded event to ensure all images are loaded.
    window.addEventListener('questionLoaded', () => { setQuestionImagesLoaded(true); });

    const vocabImages = ref.current.querySelectorAll('.vocabImage');

    if (imageModels.length === 0 && vocabImages.length === 0 && !iframe) {
      return undefined;
    }

    clearTimeout(timeout);

    timeout = setTimeout(() => {
      // Add zoom icon to all images in the iframe.
      if (iframe) {
        iframe.contentDocument.body.querySelectorAll('.fr-img-view, .trigger, .illustration').forEach((img) => {
          if (!slideshowImages.map(i => i.node).includes(img)) {
            const slideshowImage = new SlideshowImage();

            slideshowImage.init(img, true, true);
            setSlideshowImages(prev => [...prev, slideshowImage]);
          }
        });
      }
      // Add zoom icon to all images in the slide.
      ref.current.querySelectorAll('[data-zoomurl]').forEach((img) => {
        if (!slideshowImages.map(i => i.node).includes(img)) {
          if (img.getAttribute('init')) return;

          const slideshowImage = new SlideshowImage();
          slideshowImage.init(img, shouldUseZoomIcon(img as HTMLImageElement));
          setSlideshowImages(prev => [...prev, slideshowImage]);
        }
      });
      // Give time for slide to finish loading all content before initializing js.
    }, 250);
    return () => {
      setIframeLoaded(false);
      setQuestionImagesLoaded(false);
      slideshowImages.forEach(slideshowImage => slideshowImage.closeModal());
      window.removeEventListener('questionLoaded', () => { setQuestionImagesLoaded(true); });
    };
  }, [ref && ref.current && ref.current.querySelectorAll('[data-zoomurl]'),
    displaySlideGroupHeaders, iframeLoaded, questionImagesLoaded, imageModels]);
};

export default useImageZoom;
